import React from "react";
import { Check, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";

const plans = [
  {
    name: "Bronze",
    color: "#0073B6",
    icon: "🥉",
    price: "R$ 0,00",
    features: [
      { text: "Consultar projetos", included: true },
      { text: "Cadastrar projetos", included: false },
      { text: "Destaque de projeto", included: false },
      { text: "Suporte prioritário", included: false },
    ],
    button: "Seu plano atual",
    highlight: false,
  },
  {
    name: "Prata",
    color: "#0073B6",
    icon: "🥈",
    price: "R$ 59/mês",
    features: [
      { text: "Consultar projetos", included: true },
      { text: "Cadastrar 1 projeto", included: true },
      { text: "Destaque de projeto", included: false },
      { text: "Suporte prioritário", included: false },
    ],
    button: "Assinar",
    highlight: false,
  },
  {
    name: "Ouro",
    color: "#0073B6",
    icon: "🥇",
    price: "R$ 99/mês",
    features: [
      { text: "Consultar projetos", included: true },
      { text: "Cadastrar projetos ilimitados", included: true },
      { text: "Destaque de projeto", included: true },
      { text: "Suporte prioritário", included: true },
    ],
    button: "Assinar",
    highlight: true,
  },
];

const Planos: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projetosDialogOpen, setProjetosDialogOpen] = React.useState(false);
  const [cadastroDialogOpen, setCadastroDialogOpen] = React.useState(false);
  const [planoSelecionado, setPlanoSelecionado] = React.useState<string>("");

  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region', '/all-projects'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleProjetosClick = () => {
    setProjetosDialogOpen(true);
  };

  const handleAssinarClick = (plano: string) => {
    setPlanoSelecionado(plano);
    setCadastroDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-0 px-4 font-[Montserrat]">
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6 w-full">
        <div className="flex flex-wrap space-x-6 overflow-x-hidden py-3 w-full max-w-full mx-auto">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
      </div>
      {/* Texto de apresentação dos planos */}
      <div className="w-full text-left mt-16 mb-12 px-4">
        <h2 className="text-5xl font-bold text-[#0e7cb4] mb-6 leading-tight">Planos que se adaptam às suas necessidades</h2>
        <p className="text-2xl text-gray-700 mb-4 leading-snug">Flexibilidade, acesso e crescimento ao seu alcance.</p>
        <p className="text-xl text-gray-600 leading-relaxed">Nossa plataforma oferece diferentes níveis de acesso para atender diversos perfis de usuários. Escolha o plano mais adequado aos seus objetivos e aproveite os recursos que impulsionam seu desenvolvimento dentro do ambiente.</p>
      </div>
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
              </div>
            </div>
            <div className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all rounded-lg" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
              </div>
            </div>
          </div>
          <DialogFooter className="sm:justify-center">
            <button 
              className="px-4 py-2 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-medium"
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Cadastro */}
      <Dialog open={cadastroDialogOpen} onOpenChange={setCadastroDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Cadastro - Plano {planoSelecionado}</DialogTitle>
            <DialogDescription className="text-center">
              {planoSelecionado === "Bronze" 
                ? "Preencha seus dados para assinar o plano gratuito."
                : "Preencha seus dados e escolha a forma de pagamento."
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent"
                placeholder="Digite seu nome completo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent"
                placeholder="Digite seu e-mail"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <input
                type="tel"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent"
                placeholder="Digite seu telefone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CPF/CNPJ</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent"
                placeholder="Digite seu CPF ou CNPJ"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empresa (opcional)</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent"
                placeholder="Digite o nome da empresa"
              />
            </div>
            
            {/* Seção de Pagamento para planos pagos */}
            {planoSelecionado !== "Bronze" && (
              <>
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Forma de Pagamento</h3>
                  
                  {/* PIX */}
                  <div className="mb-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="payment"
                        value="pix"
                        className="text-[#0073B6] focus:ring-[#0073B6]"
                        defaultChecked
                      />
                      <span className="text-sm font-medium text-gray-700">PIX</span>
                    </label>
                    <div className="ml-6 mt-2 p-3 bg-gray-50 rounded-md">
                      <p className="text-xs text-gray-600 mb-2">Chave PIX: banclima@email.com</p>
                      <div className="bg-white p-2 rounded border">
                        <p className="text-xs text-gray-500">QR Code será gerado após confirmação</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Cartão de Crédito */}
                  <div className="mb-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="payment"
                        value="card"
                        className="text-[#0073B6] focus:ring-[#0073B6]"
                      />
                      <span className="text-sm font-medium text-gray-700">Cartão de Crédito</span>
                    </label>
                    <div className="ml-6 mt-2 space-y-2">
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent text-sm"
                        placeholder="Número do cartão"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent text-sm"
                          placeholder="MM/AA"
                        />
                        <input
                          type="text"
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent text-sm"
                          placeholder="CVV"
                        />
                      </div>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0073B6] focus:border-transparent text-sm"
                        placeholder="Nome no cartão"
                      />
                    </div>
                  </div>
                  
                  {/* Boleto */}
                  <div className="mb-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="payment"
                        value="boleto"
                        className="text-[#0073B6] focus:ring-[#0073B6]"
                      />
                      <span className="text-sm font-medium text-gray-700">Boleto Bancário</span>
                    </label>
                    <div className="ml-6 mt-2 p-3 bg-gray-50 rounded-md">
                      <p className="text-xs text-gray-600">O boleto será gerado após confirmação e enviado por e-mail</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="sm:justify-center space-x-2">
            <button 
              className="px-4 py-2 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-medium"
              onClick={() => setCadastroDialogOpen(false)}
            >
              Cancelar
            </button>
            <button 
              className="px-4 py-2 rounded bg-[#0073B6] hover:bg-[#005a8f] text-white font-medium"
              onClick={() => {
                // Aqui você pode adicionar a lógica para processar o cadastro
                if (planoSelecionado === "Bronze") {
                  alert(`Cadastro do plano ${planoSelecionado} (gratuito) realizado com sucesso!`);
                } else {
                  alert(`Cadastro do plano ${planoSelecionado} enviado! Verifique seu e-mail para instruções de pagamento.`);
                }
                setCadastroDialogOpen(false);
              }}
            >
              {planoSelecionado === "Bronze" ? "Confirmar Cadastro" : "Enviar e Pagar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <h1 className="text-3xl md:text-4xl font-bold text-center mb-8 text-[#0e7cb4] mt-12">Escolha o plano ideal para você</h1>
      <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan, idx) => (
          <div
            key={plan.name}
            className={`relative flex flex-col items-center rounded-2xl shadow-lg p-8 pt-10 bg-white border-4 transition-transform duration-300 ${
              plan.highlight
                ? "border-yellow-400 scale-105 z-10 shadow-2xl"
                : "border-transparent hover:scale-105"
            }`}
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {plan.highlight && (
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full font-semibold text-xs shadow">Mais vantajoso</span>
            )}
            <div className="text-5xl mb-2" style={{ color: plan.color }}>{plan.icon}</div>
            <h2 className="text-2xl font-bold mb-1" style={{ color: plan.color }}>{plan.name}</h2>
            <div className="text-xl font-semibold mb-4" style={{ color: plan.color }}>{plan.price}</div>
            <ul className="w-full mb-6 space-y-3">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-700 text-base">
                  {feature.included ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <X className="w-5 h-5 text-red-400" />
                  )}
                  <span className={feature.included ? "" : "line-through text-gray-400"}>{feature.text}</span>
                </li>
              ))}
            </ul>
            <button
              className={`w-full py-2 rounded-lg font-semibold text-lg transition-colors duration-200 shadow-md ${
                plan.name === "Bronze" 
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                  : "bg-[#0073B6] text-white hover:bg-[#005a8f]"
              }`}
              onClick={() => plan.name !== "Bronze" && handleAssinarClick(plan.name)}
              disabled={plan.name === "Bronze"}
            >
              {plan.button}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Planos; 